/*
 * DiveCatalog — Delivery Promise Engine, PDP block behaviour.
 * Vocabulary: semantic/SEMANTIC_LAYER.md (Stamp, PromiseTier, PickupPromise, Cutoff).
 *
 * Contract with the edge function:
 *   POST {endpoint}  {"zip":"90210","stamp":<Stamp>,"cart_value":null}
 *   200 {"ok":true,"result":{tier,mode,ship_by,deliver_by,countdown_ends,pickup,display,transit_source,service}}
 *
 * Rules this file obeys (invariant §6 "fail soft"):
 *   - Never innerHTML with API- or user-derived data. DOM is built node by node.
 *   - Any failure => the offline service matrix answers when it can (in-store
 *     Irvine stock), else the server-rendered fallback stays visible, no
 *     console.error.
 *   - Only the ZIP is ever sent. No other customer data leaves the page.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) return;
  if (window.customElements && window.customElements.get('delivery-promise')) return;

  var LS_ZIP_KEY = 'dc_promise_zip';
  var SS_GUESS_KEY = 'dc_promise_zip_guess';
  var ZIP_RE = /^\d{5}(-\d{4})?$/;
  var API_TIMEOUT_MS = 3000;
  var GEO_TIMEOUT_MS = 1500;
  var SR_REFRESH_MS = 60000; // screen-reader alternative updates at most once a minute
  var COUNTDOWN_MAX_MS = 24 * 3600e3; // past a day "order within 70:00" reads as a typo
  var DEFAULT_GEO_ENDPOINT = 'https://ipapi.co/json/';

  /* ---------------------------------------------------------------- utils */

  function debug() {
    try {
      if (window.console && console.debug) console.debug.apply(console, arguments);
    } catch (e) { /* noop */ }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = String(text);
    return n;
  }

  function clearNode(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }
  function ssGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function isZip(value) {
    return typeof value === 'string' && ZIP_RE.test(value.trim());
  }

  var DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** "2026-08-06" -> "Thu, Aug 6" (UTC-noon parse avoids timezone day-shift). */
  function formatShortDate(iso) {
    if (typeof iso !== 'string' || iso.length < 10) return '';
    var d = new Date(iso.slice(0, 10) + 'T12:00:00Z');
    if (isNaN(d.getTime())) return '';
    return DOW_SHORT[d.getUTCDay()] + ', ' + MON_SHORT[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  var DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /**
   * Date label: always a day word PLUS the date — "Today, Jul 30",
   * "Tomorrow, Jul 31", "Saturday, Aug 1", "Friday, Aug 7". The relative
   * words come from the shopper's local calendar.
   */
  function dayLabel(y, mIdx, d) {
    var now = new Date();
    var diff = Math.round((Date.UTC(y, mIdx, d) -
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 864e5);
    var dateTxt = MON_SHORT[mIdx] + ' ' + d;
    if (diff === 0) return 'Today, ' + dateTxt;
    if (diff === 1) return 'Tomorrow, ' + dateTxt;
    return DOW_FULL[new Date(Date.UTC(y, mIdx, d)).getUTCDay()] + ', ' + dateTxt;
  }

  function relLabel(iso) {
    if (typeof iso !== 'string' || iso.length < 10) return '';
    var y = Number(iso.slice(0, 4)), m = Number(iso.slice(5, 7)), d = Number(iso.slice(8, 10));
    if (!y || !m || !d) return '';
    return dayLabel(y, m - 1, d);
  }

  /**
   * Server pickup text is "today by 6pm" (kept — the time matters) or
   * "on Friday" — map the day name to its next occurrence so shoppers see
   * "Tomorrow, Jul 31" instead of a bare weekday.
   */
  function pickupLabel(text) {
    if (typeof text !== 'string' || !text) return '';
    if (text.indexOf('on ') !== 0) return text;
    var name = text.slice(3);
    var idx = DOW_FULL.indexOf(name);
    if (idx < 0) return name;
    var now = new Date();
    for (var i = 1; i <= 7; i++) {
      var cand = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
      if (cand.getDay() === idx) {
        return dayLabel(cand.getFullYear(), cand.getMonth(), cand.getDate());
      }
    }
    return name;
  }

  /* Static outline icons (stroke = currentColor). Constant markup only —
     never interpolated with API or user data. */
  var ICONS = {
    store: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 9.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5"/><path d="M4.6 3h14.8L21 7.2a2.3 2.3 0 0 1-4.5.6A2.3 2.3 0 0 1 12 7.6 2.3 2.3 0 0 1 7.5 7.8 2.3 2.3 0 0 1 3 7.2L4.6 3z"/><path d="M9.5 20v-5.5h5V20"/></svg>',
    box: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M21 7.5 12 3 3 7.5l9 4.5 9-4.5z"/><path d="M3 7.5v9L12 21l9-4.5v-9"/><path d="M12 12v9"/><path d="M7.5 5.25l9 4.5"/></svg>'
  };

  function iconNode(name) {
    var holder = el('span', 'dcdp-icon');
    holder.innerHTML = ICONS[name] || '';
    return holder;
  }

  /** "H:MM", floored at "0:00". Per brief the format stays H:MM under one hour too. */
  /* Plain units, never a clock. "21:20" reads as a time-of-day (Jason,
     2026-08-04) — "21 hr 20 min" cannot be misread. Seconds only appear
     under an hour, where they carry the urgency and the minute figure alone
     would look frozen. */
  function formatCountdown(ms) {
    if (!(ms > 0)) return '0 min';
    var total = Math.floor(ms / 1000);
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    if (h > 0) return h + ' hr ' + m + ' min';
    if (m > 0) return m + ' min ' + s + ' sec';
    return s + ' sec';
  }

  /** Spoken alternative for the ticking countdown (updated <= once/minute). */
  function humanCountdown(ms) {
    if (!(ms > 0)) return 'time is up';
    var total = Math.floor(ms / 1000);
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var parts = [];
    if (h > 0) parts.push(h + (h === 1 ? ' hour' : ' hours'));
    if (m > 0 || h === 0) parts.push(m + (m === 1 ? ' minute' : ' minutes'));
    return parts.join(' ');
  }

  /* ------------------------------------------- offline service matrix (v1)
     KEEP IN SYNC: this block is copied verbatim into delivery-promise.js and
     delivery-promise-cart.js (delivery-promise/build.py does the copy).

     When the edge function cannot answer (the Supabase project was
     restricted: HTTP 402 exceed_egress_quota 2026-09-24, exceed_db_size_quota
     2026-09-30 — even the CORS preflight fails), in-store stock at Irvine
     still gets a promise in the engine's own result shape, computed from the
     engine's service matrix (config v22, probed 2026-09-23/24 for every state
     at $20-$2,000; same rules as the Tapcart DC Cart Items v3.1 block, plus
     the $20 free-Standard edge of Shopify's General profile in CA / W1).
     Anything the matrix cannot answer returns null -> static fallback line:
     other stamp modes (dropship / jit / mcf / made_to_order need the engine's
     transit tables), heavy / oversized items (own checkout rate tables, e.g.
     Heavy Items CA Standard $22.99 under $150 — never claim FREE for them),
     an unknown ZIP3, or a carrier holiday in the window. */
  var DCDP_OFFLINE = (function () {
    var IRVINE_LOCATION_ID = '10513514611';
    var CUTOFF_HOUR = 14;            // ships same day when ordered by 2pm PT, Mon-Fri
    var PICKUP_UNTIL_HOUR = 18;      // store pickup "today by 6pm", Mon-Fri
    var FREE_STANDARD_FROM = 50;
    var SUB = 'order within {countdown}';
    var DOWS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var DOWL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // ZIP3 ranges -> zone (engine config v22)
    var W1 = [[832, 838], [840, 847], [850, 865], [889, 898], [970, 994]];                // ID UT AZ NV OR WA
    var W2 = [[570, 577], [580, 588], [590, 599], [660, 679], [680, 693], [730, 749],
              [750, 799], [800, 816], [820, 831], [870, 884], [885, 885]];              // SD ND MT KS NE OK TX CO WY NM
    var C6 = [[386, 397], [500, 528], [530, 549], [550, 567], [600, 629], [630, 658],
              [700, 714], [716, 729]];                                                  // MS IA WI MN IL MO LA AR
    var REMOTE = [[6, 9], [967, 968], [995, 999]];                                     // PR / VI, HI, AK
    var MIL = [[90, 99], [962, 966], [969, 969]];                                      // APO / FPO, Guam
    // Carrier holidays: the matrix has no holiday calendar, so a window that
    // touches one falls back to the static line instead of over-promising.
    // Observed dates; extend before 2029.
    var HOLIDAYS = {
      '2026-11-26': 1, '2026-12-25': 1, '2027-01-01': 1, '2027-05-31': 1,
      '2027-07-05': 1, '2027-09-06': 1, '2027-11-25': 1, '2027-12-24': 1,
      '2027-12-31': 1, '2028-05-29': 1, '2028-07-04': 1, '2028-09-04': 1,
      '2028-11-23': 1, '2028-12-25': 1, '2029-01-01': 1
    };

    function inRanges(z, ranges) {
      for (var i = 0; i < ranges.length; i++) {
        if (z >= ranges[i][0] && z <= ranges[i][1]) return true;
      }
      return false;
    }

    function zoneFor(zip) {
      var z = parseInt(String(zip || '').slice(0, 3), 10);
      if (isNaN(z)) return null;
      if (z >= 900 && z <= 961) return { days: 2, freeStd: 20, nd: 38.99, ca: true };
      if (inRanges(z, MIL)) return { days: 7, mil: true };
      if (inRanges(z, REMOTE)) return { days: 14, remote: true };
      if (inRanges(z, W1)) return { days: 4, freeStd: 20, nd: 41.99 };
      if (inRanges(z, W2)) return { days: 4, nd: 43.99 };
      if (inRanges(z, C6)) return { days: 6, slowDays: 7, nd: 45.99 };
      if (z >= 5 && z <= 499) return { days: 7, nd: 47.99 };
      return null;
    }

    /** Wall-clock parts in Los Angeles (DST aware) for an instant. */
    function laParts(ms) {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles', hour12: false, weekday: 'short',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric'
      }).formatToParts(new Date(ms));
      var out = {};
      for (var i = 0; i < parts.length; i++) out[parts[i].type] = parts[i].value;
      return {
        y: Number(out.year), m: Number(out.month) - 1, d: Number(out.day),
        h: Number(out.hour) % 24, mi: Number(out.minute), s: Number(out.second), // "24" = midnight on old Chrome
        dow: DOWS.indexOf(out.weekday)
      };
    }

    function day(y, m, d) {
      var t = new Date(Date.UTC(y, m, d));
      return { y: t.getUTCFullYear(), m: t.getUTCMonth(), d: t.getUTCDate(), dow: t.getUTCDay() };
    }
    function addDays(t, n) { return day(t.y, t.m, t.d + n); }
    function dayNum(t) { return Math.round(Date.UTC(t.y, t.m, t.d) / 864e5); }
    function isBiz(t) { return t.dow !== 0 && t.dow !== 6; }
    function nextBiz(t) {
      var x = addDays(t, 1);
      while (!isBiz(x)) x = addDays(x, 1);
      return x;
    }
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function iso(t) { return t.y + '-' + pad(t.m + 1) + '-' + pad(t.d); }
    function shortDate(t) { return DOWS[t.dow] + ', ' + MONS[t.m] + ' ' + t.d; }

    /** UTC ISO instant of <hour>:00 Los Angeles time on day t. */
    function laWallToIso(t, hour) {
      var target = Date.UTC(t.y, t.m, t.d, hour);
      var guess = target;
      for (var i = 0; i < 2; i++) {
        var p = laParts(guess);
        guess = target - (Date.UTC(p.y, p.m, p.d, p.h, p.mi, p.s) - guess);
      }
      return new Date(guess).toISOString();
    }

    function remoteExpedited(price) {
      return price >= 500 ? 45 : price >= 250 ? 50 : price >= 50 ? 60 : 80;
    }

    /**
     * Engine-shaped result ({tier, mode, ship_by, deliver_by, countdown_ends,
     * pickup, display, transit_source, service, fastest}) or null.
     */
    function promise(stamp, zip, cartValue, nowMs) {
      if (!stamp || typeof stamp !== 'object' || stamp.mode !== 'in_store' ||
          String(stamp.location_id) !== IRVINE_LOCATION_ID || stamp.cls) return null;
      if (typeof zip !== 'string' || !/^\d{5}(-\d{4})?$/.test(zip.trim())) return null;
      var zone = zoneFor(zip.trim());
      if (!zone) return null;
      var price = (typeof cartValue === 'number' && isFinite(cartValue) && cartValue >= 0) ? cartValue : 0;

      var now = laParts(typeof nowMs === 'number' ? nowMs : Date.now());
      if (!(now.dow >= 0) || !now.y) return null;
      var hours = now.h + now.mi / 60 + now.s / 3600;
      var today = day(now.y, now.m, now.d);
      var ship = (isBiz(today) && hours < CUTOFF_HOUR) ? today : nextBiz(today);
      // Next Day lands on Saturday for a Friday ship; only Sunday rolls to Monday.
      var nextDay = addDays(ship, 1);
      if (nextDay.dow === 0) nextDay = addDays(nextDay, 1);

      var result = {
        tier: 'firm',
        mode: 'in_store',
        ship_by: iso(ship),
        deliver_by: null,
        countdown_ends: laWallToIso(ship, CUTOFF_HOUR),
        pickup: {
          available: true,
          text: (isBiz(today) && hours < PICKUP_UNTIL_HOUR) ? 'today by 6pm' : 'on ' + DOWL[nextBiz(today).dow]
        },
        display: { headline: '', sub: SUB, checkout_suffix: '' },
        transit_source: 'offline',
        service: null,
        fastest: null
      };

      var freeNextDayFrom = zone.ca ? 150 : zone.remote ? 1000 : zone.mil ? Infinity : 300;
      var deliver;
      if (price >= freeNextDayFrom) {
        deliver = nextDay;
        result.service = { label: 'Free Next Day Shipping', days: 1, free: true };
        var overnight = dayNum(nextDay) - dayNum(ship) === 1;
        result.display.headline = (overnight ? '⚡ Overnight by ' : '🚚 Get it by ') + shortDate(nextDay);
        result.display.checkout_suffix = (overnight ? ' — overnight by ' : ' — get it by ') + shortDate(nextDay);
      } else {
        var days = (zone.slowDays && price < FREE_STANDARD_FROM) ? zone.slowDays : zone.days;
        deliver = addDays(ship, days);
        if (deliver.dow === 6) deliver = addDays(deliver, 2);
        else if (deliver.dow === 0) deliver = addDays(deliver, 1);
        var freeFrom = zone.remote ? 600 : (zone.freeStd != null ? zone.freeStd : FREE_STANDARD_FROM);
        // APO / FPO / Guam: the engine answers with its default transit and no service.
        result.service = zone.mil ? null : { label: 'Standard Shipping', days: days, free: price >= freeFrom };
        result.display.headline = '🚚 Get it by ' + shortDate(deliver);
        result.display.checkout_suffix = ' — get it by ' + shortDate(deliver);
        if (zone.remote) {
          var exp = ship;
          for (var i = 0; i < 8; i++) exp = nextBiz(exp);
          result.fastest = { label: 'Expedited Shipping', days: 8, price: remoteExpedited(price), deliver_by: iso(exp) };
        } else if (!zone.mil) {
          result.fastest = { label: 'Next Day Shipping', days: 1, price: zone.nd, deliver_by: iso(nextDay) };
        }
      }
      result.deliver_by = iso(deliver);

      var last = deliver;
      if (result.fastest) {
        var f = result.fastest.deliver_by;
        var fd = day(Number(f.slice(0, 4)), Number(f.slice(5, 7)) - 1, Number(f.slice(8, 10)));
        if (dayNum(fd) > dayNum(last)) last = fd;
      }
      for (var x = today; dayNum(x) <= dayNum(last); x = addDays(x, 1)) {
        if (HOLIDAYS[iso(x)]) return null;
      }
      return result;
    }

    return { promise: promise };
  })();

  /* Circuit breaker shared by the PDP block and the card / cart lines: after a
     failed lookup the engine is skipped for a few minutes, so every page view
     renders the offline promise at once instead of waiting on a dead host. */
  var DCDP_DOWN_KEY = 'dcdp_engine_down_until';
  var DCDP_DOWN_MS = 5 * 60 * 1000;
  function engineDown() {
    try {
      var until = Number(window.sessionStorage.getItem(DCDP_DOWN_KEY));
      return isFinite(until) && until > Date.now();
    } catch (e) { return false; }
  }
  function markEngineDown() {
    try { window.sessionStorage.setItem(DCDP_DOWN_KEY, String(Date.now() + DCDP_DOWN_MS)); } catch (e) { /* noop */ }
  }
  function markEngineUp() {
    try { window.sessionStorage.removeItem(DCDP_DOWN_KEY); } catch (e) { /* noop */ }
  }
  /* Only an unreachable engine trips the breaker: network / CORS / timeout
     errors (no status), 402 (Supabase restriction), 408, 429 and 5xx. Any
     other 4xx is a rejected payload and keeps the old static behaviour. */
  function isEngineOutage(err) {
    var status = err && err.status;
    return !status || status === 402 || status === 408 || status === 429 || status >= 500;
  }
  /* ------------------------------------------ end offline service matrix */

  /* ------------------------------------------- history hook for ?variant= */

  (function patchHistory() {
    try {
      if (window.__dcdpHistoryPatched) return;
      window.__dcdpHistoryPatched = true;
      var fire = function () {
        try { window.dispatchEvent(new CustomEvent('dcdp:locationchange')); } catch (e) { /* noop */ }
      };
      ['pushState', 'replaceState'].forEach(function (method) {
        var original = window.history && window.history[method];
        if (typeof original !== 'function') return;
        window.history[method] = function () {
          var out = original.apply(this, arguments);
          fire();
          return out;
        };
      });
      window.addEventListener('popstate', fire);
    } catch (e) {
      debug('[dcdp] history patch skipped', e);
    }
  })();

  /* ----------------------------------------------------------- networking */

  function fetchJSON(url, options, timeoutMs) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    var opts = options || {};
    if (controller) {
      opts = Object.assign({}, opts, { signal: controller.signal });
      timer = window.setTimeout(function () {
        try { controller.abort(); } catch (e) { /* noop */ }
      }, timeoutMs);
    }
    return fetch(url, opts).then(function (res) {
      if (!res.ok) {
        var err = new Error('http_' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then(function (json) {
      if (timer) window.clearTimeout(timer);
      return json;
    }, function (err) {
      if (timer) window.clearTimeout(timer);
      throw err;
    });
  }

  /* ------------------------------------------------------- custom element */

  class DeliveryPromise extends HTMLElement {}

  var proto = DeliveryPromise.prototype;

  proto.connectedCallback = function () {
    if (this._dcdpReady) return;
    this._dcdpReady = true;
    this._seq = 0;
    this._timers = [];
    this._rolledOver = false;
    try {
      this.init();
    } catch (e) {
      debug('[dcdp] init failed, fallback stays visible', e);
    }
  };

  proto.disconnectedCallback = function () {
    try {
      this.stopTimers();
      if (this._onFormChange) document.removeEventListener('change', this._onFormChange, true);
      if (this._onLocation) {
        window.removeEventListener('dcdp:locationchange', this._onLocation);
        window.removeEventListener('popstate', this._onLocation);
      }
    } catch (e) { /* noop */ }
  };

  /* -- setup ------------------------------------------------------------- */

  proto.init = function () {
    this.endpoint = this.getAttribute('data-endpoint') || '';
    var geo = this.getAttribute('data-geo-endpoint');
    this.geoEndpoint = geo === null ? DEFAULT_GEO_ENDPOINT : geo; // empty string disables geo-IP
    this.anonKey = this.getAttribute('data-anon-key') || '';
    this.fallback = this.querySelector('[data-dcdp-fallback]');
    this.variantId = String(this.getAttribute('data-variant-id') || '');
    this.stampMap = this.parseStampMap();
    this.priceMap = this.parsePriceMap();
    this.backorderMap = this.parseBackorderMap();
    this.stamp = this.parseStamp(this.getAttribute('data-stamp'));
    var attrPrice = parseFloat(this.getAttribute('data-price') || '');
    this.cartValue = isFinite(attrPrice) && attrPrice >= 0 ? attrPrice : null;

    // A deep link such as /products/x?variant=123 can disagree with the Liquid render.
    var urlVariant = this.variantFromUrl();
    if (urlVariant && urlVariant !== this.variantId &&
        Object.prototype.hasOwnProperty.call(this.stampMap, urlVariant)) {
      this.variantId = urlVariant;
      this.stamp = this.stampMap[urlVariant];
      this.cartValue = this.priceFor(urlVariant);
    }

    // No endpoint means nothing can ever load, for any variant — bail entirely.
    if (!this.endpoint) {
      debug('[dcdp] no endpoint — fallback only');
      return;
    }

    // Review BUG-1: the host DOM and the variant watchers are set up even when
    // the *currently rendered* variant carries no usable stamp. A product whose
    // first variant is out of stock (stamped {"mode":"none"}) used to return
    // here, so switching to a stamped sibling never rebound and rendered
    // nothing for the rest of the session.
    this.root = el('div', 'dcdp');
    this.root.hidden = true;
    this.promiseRegion = el('div', 'dcdp-promise');
    this.promiseRegion.setAttribute('aria-live', 'polite');
    this.zipWrap = el('div', 'dcdp-zip-wrap');
    this.root.appendChild(this.promiseRegion);
    this.root.appendChild(this.zipWrap);
    this.appendChild(this.root);

    this.bindVariantWatchers();

    if (!this.stamp) {
      // Fail soft: static copy stays, but a later applyVariant() can recover.
      debug('[dcdp] no stamp on the current variant — fallback until a stamped variant is selected');
      this.showFallback();
      return;
    }

    this.loadWhenReady();
  };

  /**
   * ZIP resolution runs at most once per element and is memoised, so a block
   * that started stampless can resolve lazily on the first stamped variant.
   * Never rejects: an unusable result resolves to null.
   */
  proto.ensureZip = function () {
    var self = this;
    if (this._zipPromise) return this._zipPromise;
    this._zipPromise = Promise.resolve()
      .then(function () { return self.resolveZip(); })
      .then(function (found) {
        self.zip = found ? found.zip : null;
        self.zipIsGuess = !!(found && found.guess);
        return self.zip;
      })
      .catch(function (e) {
        debug('[dcdp] zip resolution failed', e);
        self.zip = null;
        self.zipIsGuess = false;
        return null;
      });
    return this._zipPromise;
  };

  /** Resolve the ZIP if we don't have one yet, then load — or prompt for it. */
  proto.loadWhenReady = function () {
    var self = this;
    if (isZip(this.zip)) return this.load();
    return this.ensureZip().then(function (zip) {
      if (!isZip(zip)) {
        try { self.renderZipPrompt(); } catch (e) { /* fallback stays */ }
        return;
      }
      return self.load();
    });
  };

  proto.parseStamp = function (raw) {
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.location_id != null) return parsed;
      return null;
    } catch (e) {
      debug('[dcdp] unparseable stamp', e);
      return null;
    }
  };

  proto.parseStampMap = function () {
    var node = this.querySelector('script[data-dcdp-variant-stamps]');
    if (!node) return {};
    try {
      var map = JSON.parse(node.textContent || '{}');
      return (map && typeof map === 'object') ? map : {};
    } catch (e) {
      debug('[dcdp] unparseable variant stamp map', e);
      return {};
    }
  };

  proto.parsePriceMap = function () {
    var node = this.querySelector('script[data-dcdp-variant-prices]');
    if (!node) return {};
    try {
      var map = JSON.parse(node.textContent || '{}');
      return (map && typeof map === 'object') ? map : {};
    } catch (e) {
      debug('[dcdp] unparseable variant price map', e);
      return {};
    }
  };

  proto.parseBackorderMap = function () {
    var node = this.querySelector('script[data-dcdp-variant-backorder]');
    if (!node) return {};
    try {
      var map = JSON.parse(node.textContent || '{}');
      return (map && typeof map === 'object') ? map : {};
    } catch (e) {
      debug('[dcdp] unparseable variant backorder map', e);
      return {};
    }
  };

  /** Show the fallback line matching the current variant: backorder copy for
      continue-selling variants with zero stock, the in-stock line otherwise.
      Older Liquid renders lack the marked lines — then this does nothing. */
  proto.syncFallbackLines = function () {
    if (!this.fallback) return;
    var bo = !!(this.backorderMap && this.backorderMap[this.variantId]);
    var inStock = this.fallback.querySelector('[data-dcdp-line-instock]');
    var backorder = this.fallback.querySelector('[data-dcdp-line-backorder]');
    if (inStock) inStock.hidden = bo;
    if (backorder) backorder.hidden = !bo;
  };

  /** Variant price in dollars, or null — the service-matrix cart-value input. */
  proto.priceFor = function (variantId) {
    var value = this.priceMap ? this.priceMap[String(variantId)] : null;
    return (typeof value === 'number' && isFinite(value) && value >= 0) ? value : null;
  };

  proto.headers = function () {
    var h = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (this.anonKey) {
      h['apikey'] = this.anonKey;
      h['Authorization'] = 'Bearer ' + this.anonKey;
    }
    return h;
  };

  /* -- zip acquisition --------------------------------------------------- */

  proto.resolveZip = function () {
    var self = this;

    var customerZip = (this.getAttribute('data-customer-zip') || '').trim();
    if (isZip(customerZip)) {
      lsSet(LS_ZIP_KEY, customerZip);
      return Promise.resolve({ zip: customerZip, guess: false });
    }

    var saved = (lsGet(LS_ZIP_KEY) || '').trim();
    if (isZip(saved)) return Promise.resolve({ zip: saved, guess: false });

    var guessed = (ssGet(SS_GUESS_KEY) || '').trim();
    if (isZip(guessed)) return Promise.resolve({ zip: guessed, guess: true });

    if (!this.geoEndpoint) return Promise.resolve(null);

    // Privacy gate (security review M4): the geo-IP guess discloses the
    // shopper's IP to a third party, so it only runs when Shopify's customer
    // privacy API says analytics processing is allowed. No API → no consent
    // signal → skip the guess and fall through to the manual ZIP prompt.
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function' ||
          cp.analyticsProcessingAllowed() !== true) {
        debug('[dcdp] geo-ip skipped: no analytics consent signal');
        return Promise.resolve(null);
      }
    } catch (e) {
      return Promise.resolve(null);
    }

    return fetchJSON(this.geoEndpoint, { method: 'GET', mode: 'cors' }, GEO_TIMEOUT_MS)
      .then(function (data) {
        var postal = data && (data.postal || data.zip || data.postal_code);
        postal = typeof postal === 'string' ? postal.trim() : '';
        if (!isZip(postal)) return null;
        ssSet(SS_GUESS_KEY, postal);            // session only — a guess is never promoted
        return { zip: postal, guess: true };
      })
      .catch(function (e) {
        debug('[dcdp] geo-ip guess unavailable', e && e.message);
        return null;
      });
  };

  /* -- main load --------------------------------------------------------- */

  proto.load = function () {
    var self = this;
    if (!isZip(this.zip)) { this.renderZipPrompt(); return Promise.resolve(); }
    if (!this.stamp) { this.showFallback(); return Promise.resolve(); }

    var seq = ++this._seq;
    if (engineDown()) { this.renderOffline(); return Promise.resolve(); }
    var options = {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ zip: this.zip.trim(), stamp: this.stamp, cart_value: this.cartValue })
    };

    var attempt = function (n) {
      return fetchJSON(self.endpoint, options, API_TIMEOUT_MS).catch(function (err) {
        var status = err && err.status;
        var retriable = !(status >= 400 && status < 500); // never retry a rejected payload
        if (n < 1 && retriable) return attempt(n + 1);     // at most one retry
        throw err;
      });
    };

    return attempt(0).then(function (data) {
      if (seq !== self._seq) return;                       // superseded by a newer request
      markEngineUp();
      var result = (data && data.ok === true) ? data.result : null;
      if (!result || !result.tier || result.tier === 'none') {
        debug('[dcdp] tier none / not ok — fallback stays');
        self.showFallback();
        return;
      }
      try {
        self.renderResult(result);
      } catch (e) {
        debug('[dcdp] render failed — fallback stays', e);
        self.showFallback();
      }
    }, function (err) {
      if (seq !== self._seq) return;
      if (!isEngineOutage(err)) {                          // a rejected payload: old behaviour
        debug('[dcdp] promise lookup rejected', err && err.message);
        self.showFallback();
        return;
      }
      debug('[dcdp] engine unreachable — offline matrix', err && err.message);
      markEngineDown();
      self.renderOffline();
    });
  };

  /** Engine unreachable: same rows from the offline matrix, else the static copy. */
  proto.renderOffline = function () {
    try {
      var result = DCDP_OFFLINE.promise(this.stamp, this.zip, this.cartValue, Date.now());
      if (result) this.renderResult(result);
      else this.showFallback();
    } catch (e) {
      debug('[dcdp] offline matrix failed', e);
      this.showFallback();
    }
  };

  /* -- rendering --------------------------------------------------------- */

  proto.showFallback = function () {
    this.stopTimers();
    this._countdownNode = null;
    this._srNode = null;
    if (this.promiseRegion) clearNode(this.promiseRegion);
    if (this.zipWrap) clearNode(this.zipWrap);
    if (this.root) this.root.hidden = true;
    if (this.fallback) this.fallback.hidden = false;
    this.syncFallbackLines();
  };

  proto.renderResult = function (result) {
    var display = result.display || {};
    this.stopTimers();
    clearNode(this.promiseRegion);

    // Best-Buy-style fulfillment rows: icon + short line with a bold value.
    var tier = result.tier;
    // The core only uses the Overnight headline template when the parcel
    // truly arrives the day after it ships (a Friday ship landing Monday is
    // next-day SERVICE but not overnight to the shopper) — follow its call.
    var overnight = tier === 'firm' && result.service && result.display &&
      (result.display.headline || '').indexOf('Overnight') !== -1;

    // Row 1: store pickup, listed first like Best Buy. Shown on any tier:
    // Irvine stock can be picked up today even when SHIPPING is promised from
    // elsewhere (slow-ship oversized freight, 2026-10-22).
    if (result.pickup && result.pickup.available &&
        typeof result.pickup.text === 'string' && result.pickup.text) {
      var pickRow = el('p', 'dcdp-row dcdp-row--pickup');
      pickRow.appendChild(iconNode('store'));
      var pickBody = el('span', 'dcdp-row-text');
      // "today by 6pm" stays; "on Friday" becomes "Tomorrow, Jul 31".
      var ptext = pickupLabel(result.pickup.text);
      pickBody.appendChild(document.createTextNode('Pick up in store '));
      pickBody.appendChild(el('strong', null, ptext));
      pickRow.appendChild(pickBody);
      this.promiseRegion.appendChild(pickRow);
    }

    // Row 2: the shipping promise ("Get it tomorrow • FREE").
    var shipRow = el('p', 'dcdp-row dcdp-row--ship' + (overnight ? ' dcdp-row--overnight' : ''));
    shipRow.appendChild(iconNode('box'));
    var shipBody = el('span', 'dcdp-row-text');
    if (tier === 'range' || !result.deliver_by) {
      shipBody.appendChild(document.createTextNode(
        typeof display.headline === 'string' && display.headline
          ? display.headline
          : 'Made to order'
      ));
    } else {
      var rel = relLabel(result.deliver_by);
      shipBody.appendChild(document.createTextNode(tier === 'firm' ? 'Get it by ' : 'Estimated delivery by '));
      shipBody.appendChild(el('strong', null, rel || formatShortDate(result.deliver_by)));
      if (tier === 'firm' && result.service && result.service.free) {
        shipBody.appendChild(document.createTextNode(' • '));
        shipBody.appendChild(el('strong', 'dcdp-free', 'FREE'));
      }
    }
    shipRow.appendChild(shipBody);
    this.promiseRegion.appendChild(shipRow);

    // Service note: name the free method; overnight keeps its badge.
    if (tier === 'firm' && result.service && result.service.free &&
        typeof result.service.label === 'string' && result.service.label) {
      this.promiseRegion.appendChild(el('p', 'dcdp-free-note',
        overnight ? '⚡ Overnight — ' + result.service.label
                  : 'with ' + result.service.label));
    }

    // "Or fastest delivery Thursday — Expedited Shipping ($19.99)":
    // the paid upgrade, only present when it truly arrives earlier.
    if (result.fastest && result.fastest.deliver_by) {
      var fast = result.fastest;
      var fastRel = relLabel(fast.deliver_by) || formatShortDate(fast.deliver_by);
      if (fastRel) {
        var fastP = el('p', 'dcdp-fastest');
        fastP.appendChild(el('span', null, 'Or fastest delivery by '));
        fastP.appendChild(el('strong', null, fastRel));
        var detail = '';
        if (typeof fast.label === 'string' && fast.label) detail = ' — ' + fast.label;
        if (typeof fast.price === 'number' && fast.price > 0) {
          detail += ' ($' + fast.price.toFixed(2) + ')';
        }
        if (detail) fastP.appendChild(el('span', 'dcdp-fastest-detail', detail));
        this.promiseRegion.appendChild(fastP);
      }
    }

    if (tier === 'firm') {
      this.buildCountdown(display.sub, result.countdown_ends);
    } else if (typeof display.sub === 'string' && display.sub && display.sub.indexOf('{countdown}') === -1) {
      // estimated / range: whatever static sub the config carries
      this.promiseRegion.appendChild(el('p', 'dcdp-sub', display.sub));
    }

    this.renderZipLine();
    this.root.hidden = false;
    if (this.fallback) this.fallback.hidden = true;   // JS has replaced the static copy
  };

  /**
   * Visible countdown is aria-hidden and ticks every second; a separate
   * screen-reader line carries the same information, refreshed once a minute.
   *
   * Review BUG-4: the countdown only mounts when the cutoff is less than 24h
   * away. Beyond that the raw H:MM format renders "order within 70:00", which
   * reads as a typo and destroys the urgency the line exists to create — so the
   * whole sub-line (and its screen-reader twin) is omitted; the headline and
   * pickup line still render. The decision is made once per render; a shopper
   * sitting on the page as it crosses the boundary is out of scope.
   */
  proto.buildCountdown = function (subTemplate, countdownEnds) {
    this._countdownNode = null;
    this._srNode = null;

    var template = typeof subTemplate === 'string' ? subTemplate : '';
    var ends = countdownEnds ? Date.parse(countdownEnds) : NaN;
    if (!template || !isFinite(ends)) return;

    var remaining = ends - Date.now();
    if (remaining > 0) this._rolledOver = false; // a fresh window re-arms rollover

    if (remaining >= COUNTDOWN_MAX_MS) {
      debug('[dcdp] cutoff more than 24h out — countdown line omitted');
      return;
    }

    var parts = template.split('{countdown}');
    var line = el('p', 'dcdp-sub');
    line.setAttribute('aria-hidden', 'true');
    line.appendChild(document.createTextNode(parts[0]));

    var counter = el('span', 'dcdp-countdown');
    counter.setAttribute('aria-hidden', 'true');
    line.appendChild(counter);
    if (parts.length > 1) {
      line.appendChild(document.createTextNode(parts.slice(1).join('{countdown}')));
    }
    this.promiseRegion.appendChild(line);

    var sr = el('span', 'dcdp-sr-only');
    this.promiseRegion.appendChild(sr);

    this._countdownEnds = ends;
    this._countdownNode = counter;
    this._srNode = sr;
    this._srPrefix = parts[0];
    this._srSuffix = parts.length > 1 ? parts.slice(1).join('') : '';

    this.tickCountdown();
    this.updateSr();
    this._timers.push(window.setInterval(this.tickCountdown.bind(this), 1000));
    this._timers.push(window.setInterval(this.updateSr.bind(this), SR_REFRESH_MS));
  };

  proto.tickCountdown = function () {
    try {
      if (!this._countdownNode) return;
      var remaining = this._countdownEnds - Date.now();
      this._countdownNode.textContent = formatCountdown(remaining);
      if (remaining > 0) return;

      // Cutoff passed: refetch exactly once. The edge fn answers with the next
      // business day's ship_by/deliver_by and a new countdown_ends. No reload.
      this.stopTimers();
      if (this._rolledOver) return;
      this._rolledOver = true;
      this.load();
    } catch (e) {
      debug('[dcdp] countdown tick failed', e);
      this.stopTimers();
    }
  };

  proto.updateSr = function () {
    try {
      if (!this._srNode) return;
      var remaining = this._countdownEnds - Date.now();
      this._srNode.textContent = (this._srPrefix || '') + humanCountdown(remaining) + (this._srSuffix || '');
    } catch (e) { /* noop */ }
  };

  proto.stopTimers = function () {
    if (!this._timers) { this._timers = []; return; }
    for (var i = 0; i < this._timers.length; i++) {
      window.clearInterval(this._timers[i]);
    }
    this._timers = [];
  };

  /* -- zip UI ------------------------------------------------------------ */

  proto.renderZipLine = function () {
    var self = this;
    clearNode(this.zipWrap);

    var line = el('p', 'dcdp-zip');
    line.appendChild(document.createTextNode('Deliver to ' + this.zip + ' '));
    if (this.zipIsGuess) {
      line.appendChild(el('span', 'dcdp-guess', '(estimated from your location) '));
    }
    line.appendChild(document.createTextNode('— '));

    var button = el('button', 'dcdp-link', 'change');
    button.type = 'button';
    button.setAttribute('aria-expanded', 'false');
    line.appendChild(button);
    this.zipWrap.appendChild(line);

    var editor = this.buildZipEditor('Update');
    editor.hidden = true;
    this.zipWrap.appendChild(editor);

    button.addEventListener('click', function () {
      var open = editor.hidden;
      editor.hidden = !open;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        var input = editor.querySelector('input');
        if (input) { input.value = self.zip || ''; input.focus(); }
      }
    });
  };

  proto.renderZipPrompt = function () {
    if (!this.root) return;
    this.stopTimers();
    clearNode(this.promiseRegion);
    clearNode(this.zipWrap);
    this.zipWrap.appendChild(this.buildZipEditor('Get date', 'Enter ZIP for delivery date'));
    this.root.hidden = false;
    if (this.fallback) this.fallback.hidden = false; // fallback stays until a real promise renders
    this.syncFallbackLines();
  };

  proto.buildZipEditor = function (submitLabel, labelText) {
    var self = this;
    var wrap = el('div', 'dcdp-editor');

    var id = 'dcdp-zip-' + Math.random().toString(36).slice(2, 8);
    var label = el('label', 'dcdp-editor-label', labelText || 'ZIP code');
    label.setAttribute('for', id);

    var input = el('input', 'dcdp-input');
    input.type = 'text';
    input.id = id;
    input.name = 'dcdp_zip';
    input.autocomplete = 'postal-code';
    input.inputMode = 'numeric';
    input.maxLength = 10;
    input.placeholder = '90210';

    var submit = el('button', 'dcdp-button', submitLabel);
    submit.type = 'button';

    var error = el('span', 'dcdp-error');
    error.setAttribute('role', 'alert');

    var apply = function () {
      var value = (input.value || '').trim();
      if (!isZip(value)) {                       // validated before anything is sent
        error.textContent = 'Enter a 5-digit ZIP code.';
        input.setAttribute('aria-invalid', 'true');
        return;
      }
      error.textContent = '';
      input.removeAttribute('aria-invalid');
      self.zip = value;
      self.zipIsGuess = false;
      lsSet(LS_ZIP_KEY, value);
      ssSet(SS_GUESS_KEY, value);
      // Card + cart lines memoise their own ZIP resolution; tell them now so
      // the cart drawer on this page updates without a reload.
      try {
        window.dispatchEvent(new CustomEvent('dcdp:zipchange', { detail: { zip: value } }));
      } catch (e) { /* noop */ }
      self.load();
    };

    submit.addEventListener('click', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); apply(); }
    });

    var row = el('div', 'dcdp-editor-row');
    row.appendChild(input);
    row.appendChild(submit);
    wrap.appendChild(label);
    wrap.appendChild(row);
    wrap.appendChild(error);
    return wrap;
  };

  /* -- variant switching ------------------------------------------------- */

  proto.bindVariantWatchers = function () {
    var self = this;

    this._onFormChange = function (e) {
      try {
        var target = e && e.target;
        if (!target || typeof target.closest !== 'function') return;
        var form = target.closest('form');
        if (!form) return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;
        // Themes rewrite the hidden id input asynchronously after the change event.
        window.setTimeout(function () { self.syncFromForm(form); }, 0);
        window.setTimeout(function () { self.syncFromForm(form); }, 300);
      } catch (err) {
        debug('[dcdp] variant change handler', err);
      }
    };
    document.addEventListener('change', this._onFormChange, true);

    this._onLocation = function () {
      window.setTimeout(function () {
        try { self.applyVariant(self.variantFromUrl()); } catch (err) { debug('[dcdp] url variant', err); }
      }, 0);
    };
    window.addEventListener('dcdp:locationchange', this._onLocation);
    window.addEventListener('popstate', this._onLocation);
  };

  proto.variantFromUrl = function () {
    try {
      var value = new URLSearchParams(window.location.search).get('variant');
      return value ? String(value).replace(/\D/g, '') : null;
    } catch (e) {
      return null;
    }
  };

  proto.syncFromForm = function (form) {
    try {
      var input = form.querySelector('input[name="id"], select[name="id"]');
      if (input && input.value) this.applyVariant(String(input.value));
    } catch (e) {
      debug('[dcdp] form variant read failed', e);
    }
  };

  proto.applyVariant = function (id) {
    if (!id) return;
    id = String(id);
    if (id === this.variantId) return;
    this.variantId = id;
    this.setAttribute('data-variant-id', id);

    var stamp = Object.prototype.hasOwnProperty.call(this.stampMap, id) ? this.stampMap[id] : null;
    this.cartValue = this.priceFor(id);
    if (!stamp || typeof stamp !== 'object' || stamp.location_id == null) {
      this.stamp = null;
      this._seq++;                 // cancel anything in flight
      this.showFallback();         // no stamp for this variant => static copy
      return;
    }
    this.stamp = stamp;
    // loadWhenReady, not load: the block may have started stampless, in which
    // case no ZIP has been resolved yet (review BUG-1).
    this.loadWhenReady();
  };

  /* ------------------------------------------------------------ register */

  try {
    window.customElements.define('delivery-promise', DeliveryPromise);
  } catch (e) {
    debug('[dcdp] custom element registration failed', e);
  }
})();
